import { createAction } from '@reduxjs/toolkit';
import axios from 'axios';

import { responseEntityMapper } from '@qwealth/qcore';

import {
  ChecklistSubmission,
  ClientComplianceReview,
  ClientComplianceReviewAssigneeUpdate,
  ClientComplianceReviewQueryParams,
  ComplianceApprovalNote,
  ComplianceChecklistWithNotes,
  ComplianceNote,
  ComplianceNotesMap,
  ComplianceQuestion,
} from 'data/models/Compliance';
import { errorHandler, REACT_APP_QWEALTH_API } from 'services/axiosService';
import { complianceApprovalApi } from 'data/data-layer/kyc';
import { AppThunkDispatch } from '../store';
import {
  fetchClientsComplianceReview,
  postComplianceChecklist,
  postClientComplianceReviewAssignee,
  fetchComplianceAssignees,
  fetchClientNotesHistory,
  postComplianceReReviewNote,
} from 'data/data-layer/compliance';

export const SET_COMPLIANCE_SELECTED_CHECKLIST = 'SET_COMPLIANCE_SELECTED_CHECKLIST';
export const SET_COMPLIANCE_SELECTED_CHECKLIST_NOTES = 'SET_COMPLIANCE_SELECTED_CHECKLIST_NOTES';
export const SET_CLIENTS_COMPLIANCE_REVIEW = 'SET_CLIENTS_COMPLIANCE_REVIEW';
export const SET_COMPLIANCE_REVIEW_SELECTED_CLIENT = 'SET_COMPLIANCE_REVIEW_SELECTED_CLIENT';
export const SET_COMPLIANCE_IS_LOADING = 'SET_COMPLIANCE_IS_LOADING';
export const RESET_ALL_COMPLIANCE_INFO = 'RESET_ALL_COMPLIANCE_INFO';
export const RESET_COMPLIANCE_CONTACT_INFO = 'RESET_COMPLIANCE_CONTACT_INFO';
export const UPDATE_COMPLIANCE_CLIENT_ASSIGNEE = 'UPDATE_COMPLIANCE_CLIENT_ASSIGNEE';
export const SET_COMPLIANCE_ASSIGNEES = 'SET_COMPLIANCE_ASSIGNEES';

export const setComplianceSelectedChecklist = createAction<ComplianceQuestion[]>(SET_COMPLIANCE_SELECTED_CHECKLIST);
export const setComplianceSelectedChecklistNotes = createAction<ComplianceNotesMap>(SET_COMPLIANCE_SELECTED_CHECKLIST_NOTES);
export const setClientsComplianceReview = createAction<ClientComplianceReview[]>(SET_CLIENTS_COMPLIANCE_REVIEW);
export const setComplianceReviewSelectedClient = createAction<string | undefined>(SET_COMPLIANCE_REVIEW_SELECTED_CLIENT);
export const setComplianceIsLoading = createAction<boolean>(SET_COMPLIANCE_IS_LOADING);
export const resetAllComplianceInfo = createAction<boolean>(RESET_ALL_COMPLIANCE_INFO);
export const resetComplianceContactInfo = createAction<boolean>(RESET_COMPLIANCE_CONTACT_INFO);
export const updateClientComplianceReviewAssignee = createAction<ClientComplianceReviewAssigneeUpdate>(UPDATE_COMPLIANCE_CLIENT_ASSIGNEE);
export const setComplianceAssignees = createAction<string[]>(SET_COMPLIANCE_ASSIGNEES);

export const getComplianceChecklistForContact = async (contactQID: string): Promise<ComplianceChecklistWithNotes | undefined> => {
  try {
    return await axios.get(`${REACT_APP_QWEALTH_API}/complianceChecklist/${contactQID}`).then(response => {
      return responseEntityMapper<ComplianceChecklistWithNotes>(response);
    });
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getClientNotesHistory =
  (contactQID: string) =>
  async (dispatch: AppThunkDispatch): Promise<(ComplianceNote | ComplianceApprovalNote)[] | undefined> => {
    try {
      const response = await fetchClientNotesHistory(contactQID);
      return response;
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed fetching client notes history')(e);
      return undefined;
    }
  };

export const createComplianceReReviewNote =
  (note: ComplianceNote) =>
  async (dispatch: AppThunkDispatch): Promise<ComplianceNote | undefined> => {
    try {
      const response = await postComplianceReReviewNote(note);
      return response;
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed creating compliance re-review note')(e);
      return undefined;
    }
  };

export const getComplianceAssignees =
  () =>
  async (dispatch: AppThunkDispatch): Promise<string[] | undefined> => {
    await dispatch(setComplianceIsLoading(true));
    try {
      const response = await fetchComplianceAssignees();
      if (!response) throw new Error();
      await dispatch(setComplianceAssignees(response));
      return response;
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed fetching compliance assignees')(e);
      return undefined;
    } finally {
      dispatch(setComplianceIsLoading(false));
    }
  };

export const submitComplianceChecklist =
  (qid: string, checklistSubmission: ChecklistSubmission) =>
  async (dispatch: AppThunkDispatch): Promise<boolean | undefined> => {
    try {
      const response = await postComplianceChecklist(qid, checklistSubmission);
      if (!response) {
        throw new Error();
      } else {
        return true;
      }
    } catch (error) {
      errorHandler(dispatch, 'API Error: Failed to submit compliance checklist to review')(error);
      return undefined;
    }
  };

export const changeClientComplianceReviewAssignee =
  (qid: string, assignee: string | null) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    try {
      const response = await postClientComplianceReviewAssignee(qid, assignee);
      if (response === undefined) throw new Error();
      await dispatch(updateClientComplianceReviewAssignee({ qid: qid, assignee: response }));
    } catch (error) {
      errorHandler(dispatch, 'API Error: Failed to change assignee')(error);
    }
  };

export const resetComplianceContactRelatedInfo =
  () =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    await dispatch(setComplianceIsLoading(true));
    await dispatch(resetComplianceContactInfo(true));
    await dispatch(setComplianceIsLoading(false));
  };

export const resetComplianceData =
  () =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    await dispatch(setComplianceIsLoading(true));
    await dispatch(resetAllComplianceInfo(true));
  };

export const getClientsComplianceReview =
  (params?: ClientComplianceReviewQueryParams) =>
  async (dispatch: AppThunkDispatch): Promise<ClientComplianceReview[] | undefined> => {
    await dispatch(setComplianceIsLoading(true));
    try {
      const response = await fetchClientsComplianceReview(params);
      if (!response) throw new Error();
      await dispatch(setClientsComplianceReview(response));
      return response;
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed fetching compliance clients to review')(e);
      return undefined;
    } finally {
      dispatch(setComplianceIsLoading(false));
    }
  };

export const getComplianceChecklistsAndNotesForClient =
  (contactQID: string) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    await dispatch(setComplianceIsLoading(true));
    try {
      const response = await getComplianceChecklistForContact(contactQID);
      if (!response) throw new Error();
      await dispatch(setComplianceSelectedChecklist(response.checklist));
      await dispatch(setComplianceSelectedChecklistNotes(response.notes));
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed fetching checklist data for the specified contact')(e);
    } finally {
      dispatch(setComplianceIsLoading(false));
    }
  };

export const setSelectedClientComplianceReview =
  (contactQID: string) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    await dispatch(setComplianceIsLoading(true));
    try {
      await dispatch(setComplianceReviewSelectedClient(contactQID));
    } catch (e) {
      await errorHandler(dispatch, 'Error: Failed setting compliance contact')(e);
    } finally {
      dispatch(setComplianceIsLoading(false));
    }
  };

export const complianceApproval =
  (kycId: number) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    try {
      await complianceApprovalApi(kycId);
    } catch (e) {
      await errorHandler(dispatch, 'API Error: Failed to update compliance approval')(e);
    }
  };
